/* Public match list (home "/"). Live matches as cards on top; below, tabs All / Upcoming / Results
 * (filtered by the server: GET /api/matches?status=&q=, so paging stays correct), search and page
 * numbers. Tab / page / search live in the URL. Live cards update from /ws/public + a 10 s poll.
 * Cancelled matches are not listed publicly. */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PublicHeader from '../PublicHeader';
import Footer from '../Footer';
import usePageTitle from '../../utils/usePageTitle';
import { publicApi, TeamLogo, prettyMap, fmtDay, fmtTime } from './pubUi';
import './publicPages.css';

const PER_PAGE = 15;
const TABS = [
  { key: 'all', status: 'pending,finished' },
  { key: 'upcoming', status: 'pending' },
  { key: 'results', status: 'finished' },
];

function seriesScore(m) {
  const bo = parseInt(m.best_of, 10) || 1;
  return bo > 1
    ? { a: m.map_wins_team1 ?? 0, b: m.map_wins_team2 ?? 0, bo }
    : { a: m.score_team1 ?? 0, b: m.score_team2 ?? 0, bo };
}
function currentMapName(m) {
  let list = [];
  try { list = typeof m.map_list === 'string' ? JSON.parse(m.map_list) : (m.map_list || []); } catch (_) { }
  return prettyMap((Array.isArray(list) && list[m.current_map_index || 0]) || m.map);
}

function LiveCard({ m }) {
  const { t } = useTranslation();
  const bo = parseInt(m.best_of, 10) || 1;
  const stage = m.match_stage === 'knife' ? t('publicMatches.knife', 'Knife round') : m.match_stage === 'live' ? null : t('publicMatches.warmup', 'Warmup');
  return (
    <Link to={`/match/${m.id}`} className="pp-live">
      {m.map_image_url && <span className="pp-live-bg" style={{ backgroundImage: `url(${m.map_image_url})` }} />}
      <span className="pp-live-top">
        <span className="pp-live-badge"><span className="pp-dot" />{t('publicMatches.live', 'Live')}</span>
        <span className="pp-live-map">{currentMapName(m) || t('publicMatches.tbd', 'TBD')}{bo > 1 ? ` · ${t('publicMatches.mapOf', 'map {{n}}/{{bo}}', { n: (m.current_map_index || 0) + 1, bo })}` : ''}</span>
        {stage && <span className="pp-chip">{stage}</span>}
      </span>
      <span className="pp-live-teams">
        <span className="pp-live-team l"><TeamLogo id={m.team1_id} name={m.team1_name} avatarV={m.team1_avatar_v} size={44} /><b title={m.team1_name}>{m.team1_name}</b></span>
        <span className="pp-live-score"><b>{m.score_team1 ?? 0}</b><i>:</i><b>{m.score_team2 ?? 0}</b></span>
        <span className="pp-live-team r"><TeamLogo id={m.team2_id} name={m.team2_name} avatarV={m.team2_avatar_v} size={44} /><b title={m.team2_name}>{m.team2_name}</b></span>
      </span>
      {(bo > 1 || m.season_name) && (
        <span className="pp-live-foot">
          {bo > 1 && <span>{t('publicMatches.series', 'Series')} <b>{m.map_wins_team1 ?? 0} – {m.map_wins_team2 ?? 0}</b> · BO{bo}</span>}
          {m.season_name && <span className="pp-season">{m.season_name}</span>}
        </span>
      )}
    </Link>
  );
}

function MatchRow({ m, lng }) {
  const { t } = useTranslation();
  const done = m.status === 'finished';
  const s = seriesScore(m);
  const win = done ? (s.a > s.b ? 1 : s.b > s.a ? 2 : 0) : null;
  const when = done ? (m.end_time || m.created_at) : m.created_at;
  return (
    <Link to={`/match/${m.id}`} className={`pp-row${done ? ' done' : ''}`}>
      <span className="pp-row-date"><b>{fmtDay(when, lng)}</b><i>{fmtTime(when, lng)}</i></span>
      <span className={`pp-row-team l${win === 2 ? ' lose' : ''}${win === 1 ? ' win' : ''}`}>
        <span className="pp-row-name" title={m.team1_name}>{m.team1_name}</span>
        <TeamLogo id={m.team1_id} name={m.team1_name} avatarV={m.team1_avatar_v} size={26} />
      </span>
      <span className="pp-row-score">
        {done
          ? <><b className={win === 1 ? 'win' : ''}>{s.a}</b><i>:</i><b className={win === 2 ? 'win' : ''}>{s.b}</b></>
          : <span className="pp-vs">{t('publicMatches.vs', 'vs')}</span>}
      </span>
      <span className={`pp-row-team r${win === 1 ? ' lose' : ''}${win === 2 ? ' win' : ''}`}>
        <TeamLogo id={m.team2_id} name={m.team2_name} avatarV={m.team2_avatar_v} size={26} />
        <span className="pp-row-name" title={m.team2_name}>{m.team2_name}</span>
      </span>
      <span className="pp-row-meta">
        <span className="pp-row-format">{s.bo > 1 ? `BO${s.bo}` : (prettyMap(m.map) || t('publicMatches.tbd', 'TBD'))}</span>
        {m.season_name && <span className="pp-season" title={m.season_name}>{m.season_name}</span>}
        {!done && <span className="pp-chip amber">{t('publicMatches.upcoming', 'Upcoming')}</span>}
      </span>
      <span className="pp-row-arrow" aria-hidden="true">›</span>
    </Link>
  );
}

function pagesAround(page, total) {
  const set = new Set([1, total, page - 1, page, page + 1].filter(p => p >= 1 && p <= total));
  const list = [...set].sort((a, b) => a - b);
  const out = [];
  list.forEach((p, i) => { if (i && p - list[i - 1] > 1) out.push('…'); out.push(p); });
  return out;
}

export default function PublicMatchList() {
  const { t, i18n } = useTranslation();
  usePageTitle(t('seo.homeFullTitle', 'Flux - CS2 Tournament Panel: Live Matches, Scores and Stats'), true);
  const [params, setParams] = useSearchParams();
  const tab = TABS.find(x => x.key === params.get('tab')) || TABS[0];
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1);
  const q = params.get('q') || '';
  const [qInput, setQInput] = useState(q);
  const setParam = useCallback((patch) => setParams(prev => {
    const n = new URLSearchParams(prev);
    Object.entries(patch).forEach(([k, v]) => (v == null || v === '' || (k === 'page' && v === 1) || (k === 'tab' && v === 'all') ? n.delete(k) : n.set(k, String(v))));
    return n;
  }, { replace: true }), [setParams]);

  const [live, setLive] = useState(null);
  const [list, setList] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [counts, setCounts] = useState({});
  const [error, setError] = useState(false);

  // debounce the search box into the URL
  useEffect(() => { setQInput(q); }, [q]);
  useEffect(() => {
    if (qInput === q) return undefined;
    const id = setTimeout(() => setParam({ q: qInput.trim(), page: 1 }), 300);
    return () => clearTimeout(id);
  }, [qInput, q, setParam]);

  const loadLive = useCallback(async () => {
    try { const r = await publicApi.get('/api/matches', { params: { status: 'live', limit: 12 } }); setLive(r.data?.data || []); } catch (_) { setLive(l => l || []); }
  }, []);
  const loadCounts = useCallback(async () => {
    const total = async (status) => {
      try { const r = await publicApi.get('/api/matches', { params: { status, limit: 1, ...(q ? { q } : {}) } }); return r.data?.pagination?.total ?? 0; } catch (_) { return null; }
    };
    const [all, upcoming, results] = await Promise.all(TABS.map(x => total(x.status)));
    setCounts({ all, upcoming, results });
  }, [q]);
  const loadList = useCallback(async () => {
    try {
      const r = await publicApi.get('/api/matches', { params: { status: tab.status, page, limit: PER_PAGE, ...(q ? { q } : {}) } });
      setList(r.data?.data || []);
      setTotalPages(Math.max(1, r.data?.pagination?.totalPages || 1));
      setError(false);
    } catch (_) { setError(true); setList(l => l || []); }
  }, [tab.status, page, q]);

  useEffect(() => { loadLive(); }, [loadLive]);
  useEffect(() => { loadCounts(); }, [loadCounts]);
  useEffect(() => { setList(null); loadList(); }, [loadList]);

  // live updates: WS for scores/stages, a match starting/finishing reloads everything; 10 s poll as a fallback
  const reloadAll = useRef(() => {});
  reloadAll.current = () => { loadLive(); loadCounts(); loadList(); };
  useEffect(() => {
    let ws = null; let retry = null; let alive = true;
    const connect = () => {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (host === 'localhost' || host === '127.0.0.1') ? ':5000' : (window.location.port ? `:${window.location.port}` : '');
      ws = new WebSocket(`${proto}//${host}${port}/ws/public`);
      ws.onmessage = (ev) => {
        try {
          const msg = JSON.parse(ev.data);
          if (msg.type === 'tech_pause' && msg.data) window.dispatchEvent(new CustomEvent('techPause', { detail: msg.data }));
          if (!msg.data || !msg.data.match_id) return;
          if (msg.type === 'score_update') setLive(p => p && p.map(m => m.id === msg.data.match_id ? { ...m, score_team1: msg.data.score_team1, score_team2: msg.data.score_team2 } : m));
          else if (msg.type === 'match_stage_change') setLive(p => p && p.map(m => m.id === msg.data.match_id ? { ...m, match_stage: msg.data.stage } : m));
          else if (msg.type === 'match_started' || msg.type === 'match_finished') reloadAll.current();
        } catch (_) { }
      };
      ws.onclose = () => { if (alive) retry = setTimeout(connect, 3000); };
    };
    connect();
    const poll = setInterval(() => { if (!document.hidden) loadLive(); }, 10000);
    return () => { alive = false; clearTimeout(retry); clearInterval(poll); if (ws) ws.close(); };
  }, [loadLive]);

  const liveCount = live ? live.length : 0;

  return (
    <div className="pub-root pp-root">
      <PublicHeader />
      <div className="pp-wrap">
        <div className="pp-head">
          <div>
            <h1>{t('publicMatches.title', 'Matches')}</h1>
            <p>{t('publicMatches.subtitle', 'Live scores and results, updated in real time.')}</p>
          </div>
          {liveCount > 0 && <span className="pp-live-count"><span className="pp-dot" />{t('publicMatches.liveCount', 'Live now: {{count}}', { count: liveCount })}</span>}
        </div>

        {live && live.length > 0 && (
          <section className="pp-live-grid">{live.map(m => <LiveCard key={m.id} m={m} />)}</section>
        )}

        <div className="pp-toolbar">
          <div className="pp-tabs" role="tablist">
            {TABS.map(x => (
              <button key={x.key} type="button" role="tab" aria-selected={x.key === tab.key} className={`pp-tab${x.key === tab.key ? ' on' : ''}`} onClick={() => setParam({ tab: x.key, page: 1 })}>
                {t(`publicMatches.tab_${x.key}`, { all: 'All', upcoming: 'Upcoming', results: 'Results' }[x.key])}
                {counts[x.key] != null && <span className="pp-tab-count">{counts[x.key]}</span>}
              </button>
            ))}
          </div>
          <label className="pp-search">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
            <input value={qInput} onChange={e => setQInput(e.target.value)} placeholder={t('publicMatches.searchPh', 'Search team…')} />
            {qInput && <button type="button" onClick={() => setQInput('')} aria-label={t('common.clear', 'Clear')}>×</button>}
          </label>
        </div>

        <div className="pp-list">
          {list === null ? (
            Array.from({ length: 6 }).map((_, i) => <div key={i} className="pp-row skel" />)
          ) : error && !list.length ? (
            <div className="pp-empty">{t('publicMatches.loadFailed', 'Could not load matches. Retrying…')}</div>
          ) : list.length === 0 ? (
            <div className="pp-empty">
              {q ? t('publicMatches.noMatch', 'No matches for “{{q}}”.', { q })
                : tab.key === 'upcoming' ? t('publicMatches.noUpcoming', 'No upcoming matches.')
                : tab.key === 'results' ? t('publicMatches.noResults', 'No results yet.')
                : t('publicMatches.noMatches', 'No matches yet. Check back soon.')}
            </div>
          ) : list.map(m => <MatchRow key={m.id} m={m} lng={i18n.language} />)}
        </div>

        {totalPages > 1 && (
          <nav className="pp-pager" aria-label={t('publicMatches.pages', 'Pages')}>
            <button type="button" disabled={page <= 1} onClick={() => setParam({ page: page - 1 })} aria-label={t('common.previous', 'Previous')}>‹</button>
            {pagesAround(page, totalPages).map((p, i) => p === '…'
              ? <span key={`e${i}`} className="pp-pager-gap">…</span>
              : <button key={p} type="button" className={p === page ? 'on' : ''} onClick={() => setParam({ page: p })}>{p}</button>)}
            <button type="button" disabled={page >= totalPages} onClick={() => setParam({ page: page + 1 })} aria-label={t('common.next', 'Next')}>›</button>
          </nav>
        )}
      </div>
      <Footer />
    </div>
  );
}
